import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./index.scss";
import ReactPaginate from "react-paginate";

import CreateSupplierComponent from "@components/CreateSupplierComponent/CreateSupplierComponent";
import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import CustomDialog from "@components/CustomDialog/CustomDialog";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import { CreateModalContext } from "layouts/appLayout/layout";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useDisableSupplierMutation,
  useEnableSupplierMutation,
  useGetSuppliersQuery,
} from "store/supplier/supplier.api";
import { useNavigate } from "react-router-dom";

const SuppliersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const [openMenuId, setOpenMenuId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: suppliers, isLoading: isLoadingSuppliers } =
    useGetSuppliersQuery(companyId);

  const [disableSupplier, { isLoading: isDeleting }] =
    useDisableSupplierMutation();
  const [enableSupplier] = useEnableSupplierMutation();

  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");

  const { toggleCreateModal } = useContext(CreateModalContext);

  // ✅ Permissions
  const canEditSupplier =
    currentUser?.roleId?.permissions.includes("EDIT_SUPPLIER");
  const canChangeStatus =
    currentUser?.roleId?.permissions.includes("EDIT_SUPPLIER");
  const canViewSupplier =
    currentUser?.roleId?.permissions.includes("CREATE_SUPPLIER");

  const headers = [
    { key: "name", label: t("supplier.form.name") },
    { key: "email", label: t("supplier.form.email") },
    { key: "status", label: t("supplier.table.status") },
    { key: "createdAt", label: t("supplier.table.createdAt") },
  ];

  const renderCell = (key, supplier) => {
    if (key === "status") return supplier.status === "enabled" ? "✅" : "❌";
    if (key === "createdAt")
      return new Date(supplier.createdAt).toLocaleDateString();
    return supplier[key];
  };

  const handleEditSupplier = (supplier) => {
    if (!canEditSupplier) return toast.error(t("supplier.no_permission"));
    setOpenMenuId(null);
    toggleCreateModal(
      <CreateSupplierComponent
        onClose={() => toggleCreateModal(<></>)}
        supplier={supplier}
        isUpdate={true}
      />,
      t("supplier.form.title")
    );
  };

  const handleToggleSupplierStatus = async (supplier) => {
    if (!canChangeStatus) return toast.error(t("supplier.no_permission"));
    try {
      if (supplier?.status === "enabled") {
        await disableSupplier(supplier?._id).unwrap();
        toast.success(t("supplier.disable_success"));
      } else {
        await enableSupplier(supplier?._id).unwrap();
        toast.success(t("supplier.enable_success"));
      }
    } catch (error) {
      toast.error(t("supplier.status_change_error"));
    }
  };

  const actionMenu = (supplier) => {
    const isEnabled = supplier?.status === "enabled";

    const actions = [];

    if (canEditSupplier) {
      actions.push({
        label: t("supplier.actions.edit"),
        onClick: () => handleEditSupplier(supplier),
      });
    }

    if (canChangeStatus) {
      actions.push({
        label: isEnabled
          ? t("supplier.actions.disable")
          : t("supplier.actions.enable"),
        onClick: () => handleToggleSupplierStatus(supplier),
      });
    }

    if (canViewSupplier) {
      actions.push({
        label: t("supplier.actions.consulter"),
        onClick: () => navigate(`/details-supplier/${supplier?._id}`),
      });
    }

    return (
      <CustomActionMenu
        openMenuId={openMenuId}
        setOpenMenuId={setOpenMenuId}
        data={supplier}
        actions={actions}
      />
    );
  };

  const onSearchSupplier = (text) => setSearchText(text.toLowerCase());

  const filteredSuppliers = useMemo(() => {
    if (!suppliers?.data) return [];
    if (searchText.length <= 1) return suppliers.data;

    return suppliers.data.filter((supplier) => {
      const name = supplier.name?.toLowerCase() || "";
      const email = supplier.email?.toLowerCase() || "";
      return name.includes(searchText) || email.includes(searchText);
    });
  }, [suppliers?.data, searchText]);

  // ✅ PAGINATION (comme Avoir)
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const pageCount = Math.ceil(filteredSuppliers.length / itemsPerPage);

  const paginatedSuppliers = filteredSuppliers.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  if (isLoadingSuppliers) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("supplier.header_title")}
        dataLength={filteredSuppliers.length}
        onSearch={onSearchSupplier}
      />

      <CustomTable
        headers={headers}
        data={paginatedSuppliers}
        rowKey="_id"
        renderCell={renderCell}
        actionMenu={actionMenu}
      />

      {/* Pagination avec ReactPaginate */}

      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <ReactPaginate
          previousLabel={"←"}
          nextLabel={"→"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      </div>

      <CustomDialog
        open={isDeleteDialogOpen}
        setOpen={setIsDeleteDialogOpen}
        title={t("supplier.delete_dialog.title")}
        description={t("supplier.delete_dialog.description")}
        cancelText={t("supplier.delete_dialog.cancel")}
        submitText={t("supplier.delete_dialog.submit")}
        onSubmit={async () => {
          if (!canChangeStatus) return toast.error(t("supplier.no_permission"));
          try {
            await disableSupplier(supplierToDelete._id).unwrap();
            setIsDeleteDialogOpen(false);
            toast.success(t("supplier.delete_success"));
          } catch (error) {
            toast.error(t("supplier.delete_error"));
          }
        }}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SuppliersPage;
